import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useCanvas } from "../../../../context/CanvasContext";
import ConfirmationModal from "../../../../../shared/components/modals/ConfirmationModal";
import EditModal from "../../../../../shared/components/modals/EditModal";
import InstructionsModal from "../../../../../shared/components/InstructionsModal";
import { toast } from "react-toastify";
import {
  fetchBudgets,
  createBudget,
  updateBudget,
  deleteBudget,
  uploadBudgets,
  selectBudgets,
  selectBudgetLoading,
  selectBudgetPagination,
  budgetOverview, selectBudgetOverview,
} from "../../../../../../app/store/slices/budgetSlice";
import { selectAccounts, fetchAccounts } from "../../../../../../app/store/slices/accountSlice";
import axios from "axios";
import Pagination from '../../../../../shared/components/Pagination';
import FileUploadButton from '../../../../../shared/components/FileUploadButton';
import ActionButtons from '../../../../../shared/components/ActionButton';
import useClickOutside from "../../../../../shared/hooks/useClickOutside";
import {PAGE_SIZE_OPTIONS, FREQUENCY_OPTIONS, budgetTableColumns } from '../../../../../../utils/constants/dashboardConstants';
import TableHeader from '../../../../../shared/components/TableHeader';
import TableBody from '../../../../../shared/components/TableBody';
import EditableCell from '../../../../../shared/components/EditableCell';
import SummaryCard from "../../../../../shared/components/SummaryCard";
import {FiCheck } from "react-icons/fi";
import { FaMoneyCheckAlt } from "react-icons/fa";
import { GiExpense} from 'react-icons/gi';
import { TbChartPieFilled } from 'react-icons/tb';
import AddOptionsButton from "../../../../../shared/components/AddOptionsButton";
import { CATEGORIES } from "../../../../../../utils/constants/dashboardConstants";
import { formatAmount} from '../../../../../../utils/formatAmount';



const BudgetContent = () => {
  const dispatch = useDispatch();
  const { selectedCanvas } = useCanvas();
  const budgets = useSelector(selectBudgets);
  const loading = useSelector(selectBudgetLoading);
  const pagination = useSelector(selectBudgetPagination);
  const accounts = useSelector(selectAccounts);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  // Modal states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showAddOptions, setShowAddOptions] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  // Data states
  const [selectedBudget, setSelectedBudget] = useState(null);
  const [editingCells, setEditingCells] = useState({});
  const [updatingCells, setUpdatingCells] = useState({});
  
  const [editBudgetData, setEditBudgetData] = useState({
    itemName: "",
    category: "",
    frequency: "",
    allocationPercent: "",
    plannedCost: "",
    actualCost: "",
    paymentAccountId: "",
    tags: "",
  });
  const [addBudgetData, setAddBudgetData] = useState({
    itemName: "",
    category: "",
    frequency: "",
    allocationPercent: "",
    plannedCost: "",
    actualCost: "",
    paymentAccountId: "",
    tags: "",
  });

  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const fileInputRef = useRef();
  const overview = useSelector(selectBudgetOverview);
  const [budgetError, setBudgetError] = useState("");

  const baseCurrency = selectedCanvas?.currency || ""; 
  const [conversionRates, setConversionRates] = useState({});
  const addOptionsRef = useRef(null);
  useClickOutside(addOptionsRef, () => setShowAddOptions(false), showAddOptions);

  const getCurrentMonth = () => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[new Date().getMonth()];
  };

  // Color helpers
  const getOverviewColor = (type, value, comparison) => {
    switch (type) {
      case 'allocated':
        if (value < comparison) return 'text-green-600';
        if (value === comparison) return 'text-gray-900';
        return 'text-red-600';
      case 'spent':
        if (value < comparison) return 'text-green-600';
        if (value === comparison) return 'text-gray-900';
        return 'text-red-600';
      case 'available':
        return value < 0 ? 'text-red-600' : 'text-gray-900';
      default:
        return 'text-gray-900';
    }
  };

  const getActualCostColor = (actualCost, plannedCost) => {
    if (actualCost < plannedCost) return 'text-green-600';
    if (actualCost === plannedCost) return 'text-gray-900';
    return 'text-red-600';
  };

  const formatCurrency = (amount) => {
    return `${baseCurrency} ${formatAmount(amount)}`;
  };

  const handleCellEdit = (budgetId, field) => {
    setEditingCells(prev => ({ ...prev, [`${budgetId}-${field}`]: true }));
  };

  const handleCellSave = async (budgetId, field, value) => {
    const cellKey = `${budgetId}-${field}`;
    setUpdatingCells(prev => ({ ...prev, [cellKey]: true }));
    
    try {
      const updateData = { [field]: Number(value) };
      await dispatch(updateBudget({
        canvasId: selectedCanvas.id,
        budgetId,
        updateData
      })).unwrap();
      
      setEditingCells(prev => ({ ...prev, [cellKey]: false }));
      toast.success(`${field === 'plannedCost' ? 'Planned cost' : 'Actual cost'} updated successfully`);
    } catch (err) {
      toast.error(err?.message || 'Failed to update');
      setEditingCells(prev => ({ ...prev, [cellKey]: false }));
    } finally {
      setUpdatingCells(prev => ({ ...prev, [cellKey]: false }));
    }
  };

  const handleCellCancel = (budgetId, field) => {
    setEditingCells(prev => ({ ...prev, [`${budgetId}-${field}`]: false }));
  };

  const handleDownloadTemplate = () => {
    try {
      const sampleAccountName = accounts.length > 0 ? accounts[0].name : 'account_name_here';
      
      const templateData = [
        { 
          itemName: 'Rent', 
          category: 'Housing', 
          frequency: 'Monthly', 
          allocationPercent: '30', 
          plannedCost: '1500', 
          actualCost: '', 
          paymentAccountId: sampleAccountName,
          tags: ''
        },
        { 
          itemName: 'Netflix', 
          category: 'Entertainment', 
          frequency: 'Monthly', 
          allocationPercent: '2', 
          plannedCost: '15', 
          actualCost: '15', 
          paymentAccountId: sampleAccountName,
          tags: 'Subscription'
        }
      ];

      const csvContent = [
        'itemName,category,frequency,allocationPercent,plannedCost,actualCost,paymentAccountId,tags',
        ...templateData.map(row => 
          `${row.itemName},${row.category},${row.frequency},${row.allocationPercent},${row.plannedCost},${row.actualCost},${row.paymentAccountId},${row.tags}`
        )
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `budget_template_${baseCurrency}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      setShowInstructions(false);
    } catch (error) {
      toast.error('Failed to download template');
      console.error('Template download error:', error);
    }
  };

  useEffect(() => {
    const fetchRates = async () => {
      if (!baseCurrency || !accounts?.length) {
        setConversionRates({});
        return;
      }
      const uniqueCurrencies = [
        ...new Set(accounts.map(acc => acc.currency).filter(cur => cur && cur !== baseCurrency))
      ];
      if (uniqueCurrencies.length === 0) {
        setConversionRates({});
        return;
      }
      try {
        const res = await axios.get(
          `https://api.exchangerate-api.com/v4/latest/${baseCurrency}`,
        );
        setConversionRates(res.data.rates || {});
      } catch (err) {
        setConversionRates({});
      }
    };
    fetchRates();
  }, [accounts, baseCurrency]);

  useEffect(() => {
    if (selectedCanvas?.id) {
        dispatch(budgetOverview({ canvasId: selectedCanvas?.id }));
        dispatch(fetchAccounts({canvasId:selectedCanvas?.id, limi:1000}))
    }
    }, [dispatch, selectedCanvas?.id, budgets]);

  useEffect(() => {
    if (selectedCanvas?.id) {
      dispatch(fetchBudgets({ canvasId: selectedCanvas.id, page: currentPage, limit: pageSize }));
    }
  }, [dispatch, selectedCanvas?.id, currentPage, pageSize]);

  useEffect(() => {
    if (showEditForm && selectedBudget) {
      setEditBudgetData({
        itemName: selectedBudget.itemName || "",
        category: selectedBudget.category || "",
        frequency: selectedBudget.frequency || "",
        allocationPercent: selectedBudget.allocationPercent ?? "",
        plannedCost: selectedBudget.plannedCost ?? "",
        actualCost: selectedBudget.actualCost ?? "",
        paymentAccountId: selectedBudget.paymentAccountId || "",
        tags:  Array.isArray(selectedBudget.tags) && selectedBudget.tags.includes("Subscription") ? "Subscription" : ""
      });
    }
  }, [showEditForm, selectedBudget]);

  const handleDeleteBudget = async () => {
    if (!selectedBudget || !selectedCanvas?.id) {
      toast.error("Invalid Canvas or Item");
      return;
    }
    setIsDeleting(true);
    try {
      await dispatch(
        deleteBudget({
          canvasId: selectedCanvas.id,
          budgetId: selectedBudget._id,
        })
      ).unwrap();
      dispatch(fetchBudgets({ canvasId: selectedCanvas.id, page: currentPage, limit: pageSize }));
      setShowDeleteConfirm(false);
      setSelectedBudget(null);
      toast.success("Budget Item Deleted Successfully");
    } catch (err) {
      toast.error(err?.message || err);
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle Edit
  const handleEditBudget = async (e) => {
    e.preventDefault();
    if (!selectedBudget || !selectedCanvas?.id) {
      toast.error("Invalid Canvas or Item");
      return;
    }
    setIsEditing(true);
    try {
      await dispatch(
        updateBudget({
          canvasId: selectedCanvas.id,
          budgetId: selectedBudget._id,
          updateData: {
            ...editBudgetData,
            allocationPercent: Number(editBudgetData.allocationPercent),
            plannedCost: Number(editBudgetData.plannedCost),
            actualCost: editBudgetData.actualCost ? Number(editBudgetData.actualCost) : undefined,
            tags: editBudgetData.tags === "Subscription" ? ["Subscription"] : []
          },
        })
      ).unwrap();
      setShowEditForm(false);
      setSelectedBudget(null);
      toast.success("Budget Item Updated Successfully");
    } catch (err) {
      toast.error(err?.message || err);
    } finally {
      setIsEditing(false);
    }
  };

  // Handle Add
  const handleAddBudget = async (e) => {
    e.preventDefault();
    if (!selectedCanvas?.id) {
      toast.error("Invalid Canvas");
      return;
    }
    setIsAdding(true);
    try {
      await dispatch(
        createBudget({
          canvasId: selectedCanvas.id,
          budgetData: {
            ...addBudgetData,
            allocationPercent: Number(addBudgetData.allocationPercent),
            plannedCost: Number(addBudgetData.plannedCost),
            actualCost: addBudgetData.actualCost ? Number(addBudgetData.actualCost) : undefined,
            tags: addBudgetData.tags === "Subscription" ? ["Subscription"] : [],
          },
        })
      ).unwrap();
      setShowAddForm(false);
      setAddBudgetData({
        itemName: "",
        category: "",
        frequency: "",
        allocationPercent: "",
        plannedCost: "",
        actualCost: "",
        paymentAccountId: "",
        tags: "",
      });
      toast.success("Budget Item Created Successfully");
    } catch (err) {
      toast.error(err?.message || err);
    } finally {
      setIsAdding(false);
    }
  };

  // Handle file upload
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedCanvas?.id) {
      toast.error("Invalid Canvas ID or file");
      return;
    }
    try {
      await dispatch(uploadBudgets({ canvasId: selectedCanvas.id, file })).unwrap();
      toast.success("Budget Items Imported Successfully");
    } catch (err) {
      toast.error(err?.message || err);
    }
  };

  const [categorySuggestions, setCategorySuggestions] = useState([]);
  const [showCategorySuggestions, setShowCategorySuggestions] = useState(false);

  const handleCategoryChange = (value, isEdit = false) => {
    if (isEdit) {
      setEditBudgetData({ ...editBudgetData, category: value });
    } else {
      setAddBudgetData({ ...addBudgetData, category: value });
    }

    if (value.length > 0) {
      const filtered = CATEGORIES.filter(cat =>
        cat.toLowerCase().includes(value.toLowerCase())
      );
      setCategorySuggestions(filtered);
      setShowCategorySuggestions(true);
    } else {
      setShowCategorySuggestions(false);
    }
  };

  const selectCategory = (category, isEdit = false) => {
    if (isEdit) {
      setEditBudgetData({ ...editBudgetData, category });
    } else {
      setAddBudgetData({ ...addBudgetData, category });
    }
    setShowCategorySuggestions(false);
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-4 md:mb-6">
        <h1 className="text-xl md:text-2xl font-bold">Budget - {getCurrentMonth()}</h1>
      </div>
      
      {/* Table Container  */}
      <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm">
        {/* Overview Header  */}
        <div className="flex flex-wrap items-center justify-center md:justify-between gap-4 md:gap-8 mb-6">
          {/* Cards Row */}
          <div className="flex flex-wrap gap-4 md:gap-8 w-full md:w-auto">
            {/* Monthly Income */}
           <SummaryCard
              title="Monthly Income"
              value={overview?.totalIncome !== undefined ? overview.totalIncome : "Auto calculated"}
              icon={<FaMoneyCheckAlt className="w-5 h-5" />}
              valueColor="text-blue-700"
              iconBg="bg-blue-100"
              iconColor="text-blue-600"
              formatValue={formatCurrency}
            />
            <SummaryCard
              title="Available to Allocate"
              value={overview?.available !== undefined ? overview.available : "Auto calculated"}
              icon={<FiCheck className="w-5 h-5" />}
              valueColor={getOverviewColor('available', overview?.available)}
              iconBg="bg-green-100"
              iconColor="text-green-600"
              formatValue={formatCurrency}
            />
            <SummaryCard
              title="Allocated"
              value={overview?.allocated !== undefined ? overview.allocated : "Auto calculated"}
              icon={<TbChartPieFilled className="w-5 h-5" />}
              valueColor={getOverviewColor('allocated', overview?.allocated, overview?.available)}
              iconBg="bg-yellow-100"
              iconColor="text-yellow-600"
              formatValue={formatCurrency}
            />
            <SummaryCard
              title="Actual Spend"
              value={overview?.spent !== undefined ? overview.spent : "Auto calculated"}
              icon={<GiExpense  className="w-5 h-5" />}
              valueColor={getOverviewColor('spent', overview?.spent, overview?.allocated)}
              iconBg="bg-red-100"
              iconColor="text-red-600"
              formatValue={formatCurrency}
            />
          </div>
          
          {/* Add Budget Item Button */}
          <AddOptionsButton
              showOptions={showAddOptions}
              setShowOptions={setShowAddOptions}
              addOptionsRef={addOptionsRef}
              onAddManual={() => setShowAddForm(true)}
              fileInputRef={fileInputRef}
              handleFileChange={handleFileChange}
              onShowInstructions={() => setShowInstructions(true)}
              buttonLabel="Add Item"
              buttonTitle="Add Item"
              manualLabel="Add Manually"
              FileUploadButtonComponent={FileUploadButton}
            />
        </div>

        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <div className="inline-block min-w-full align-middle">
            <div className="overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <TableHeader columns={budgetTableColumns}/>
                <TableBody
                  loading={loading}
                  data={budgets}
                  columnsCount={9}
                  emptyMessage="No Item found."
                  loadingMessage="Loading Items..."
                    renderRow={budget => (
                      <tr key={budget?._id}>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">{budget.itemName}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{budget.category}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{budget.frequency}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{budget.allocationPercent}%</td>
                        
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                          <EditableCell
                            value={budget.plannedCost}
                            isEditing={editingCells[`${budget._id}-plannedCost`]}
                            isUpdating={updatingCells[`${budget._id}-plannedCost`]}
                            onEdit={() => handleCellEdit(budget._id, 'plannedCost')}
                            onSave={(value) => handleCellSave(budget._id, 'plannedCost', value)}
                            onCancel={() => handleCellCancel(budget._id, 'plannedCost')}
                            inputType="number"
                            inputProps={{ min: 0, step: 0.01 }}
                            displayFormat={formatCurrency}
                            editTitle="Click to edit planned cost"
                          />
                        </td>

                        {/* Editable Actual Cost */}
                        <td className="px-3 py-4 whitespace-nowrap text-sm">
                          <EditableCell
                            value={budget.actualCost || 0}
                            isEditing={editingCells[`${budget._id}-actualCost`]}
                            isUpdating={updatingCells[`${budget._id}-actualCost`]}
                            onEdit={() => handleCellEdit(budget._id, 'actualCost')}
                            onSave={(value) => handleCellSave(budget._id, 'actualCost', value)}
                            onCancel={() => handleCellCancel(budget._id, 'actualCost')}
                            inputType="number"
                            inputProps={{ min: 0, step: 0.01 }}
                            displayFormat={(val) => val ? formatCurrency(val) : 0}
                            displayClassName={`cursor-pointer hover:bg-gray-50 px-2 py-1 rounded ${getActualCostColor(budget.actualCost || 0, budget.plannedCost || 0)}`}
                            editTitle="Click to edit actual cost"
                          />
                        </td>

                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                         {Array.isArray(budget.tags) && budget.tags.length > 0 
                            ? budget.tags.join(', ') 
                            : ''
                          }
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                          {accounts.find(a => a._id === budget.paymentAccountId)?.name || "—"}
                        </td>
                         <td className="px-3 py-4 whitespace-nowrap text-sm">
                          <ActionButtons
                            onEdit={() => {
                              setSelectedBudget(budget);
                              setShowEditForm(true);
                            }}
                            onDelete={() => {
                              setSelectedBudget(budget);
                              setShowDeleteConfirm(true);
                            }}
                          />
                        </td>
                      </tr>
                    )}
                  />
              </table>
              {/* Pagination Controls */}
              <Pagination
                pagination={pagination}
                pageSize={pageSize}
                setPageSize={setPageSize}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                PAGE_SIZE_OPTIONS={PAGE_SIZE_OPTIONS}
                label="Items per page"
                totalLabel={`Total: ${pagination?.total || 0} items`}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        show={showDeleteConfirm}
        title="Delete Budget Item"
        message={`Are you sure you want to delete "${selectedBudget?.itemName}"? This action cannot be undone.`}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setSelectedBudget(null);
        }}
        onConfirm={handleDeleteBudget}
        loading={isDeleting}
      />

      {/* Edit Modal */}
      <EditModal
        show={showEditForm}
        title="Edit Item"
        onCancel={() => {
          setShowEditForm(false);
          setSelectedBudget(null);
        }}
        onSubmit={handleEditBudget}
        loading={isEditing}
        submitLabel="Update"
      >
        <div className="space-y-4 max-w-md w-full max-h-[80vh] overflow-y-auto p-2 sm:p-0">
          {budgetError && (
            <div className="text-yellow-600 text-sm font-semibold mb-2">{budgetError}</div>
          )}
          <div>
            <label className="block text-lg font-medium text-gray-700 mb-1">Item</label>
            <input
              type="text"
              value={editBudgetData.itemName}
              onChange={e => setEditBudgetData({ ...editBudgetData, itemName: e.target.value })}
              className="w-full px-3 py-2 border rounded"
              required
            />
          </div>
          <div>
            <label className="block text-lg font-medium text-gray-700 mb-1">Category</label>
            <div className="relative">
              <input
                type="text"
                value={editBudgetData.category}
                onChange={e => handleCategoryChange(e.target.value, true)}
                className="w-full px-3 py-2 border rounded"
                required
              />
              {showCategorySuggestions && editBudgetData.category && (
                <div className="absolute z-10 bg-white border rounded shadow-md mt-1 max-h-60 overflow-auto w-full">
                  {categorySuggestions.length > 0 ? (
                    categorySuggestions.map((cat, idx) => (
                      <div
                        key={idx}
                        className="px-4 py-2 text-sm cursor-pointer hover:bg-gray-100"
                        onClick={() => selectCategory(cat, true)}
                      >
                        {cat}
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-2 text-sm text-gray-500">No suggestions found</div>
                  )}
                </div>
              )}
            </div>
          </div>
          <div>
            <label className="block text-lg font-medium text-gray-700 mb-1">Frequency</label>
            <select
              value={editBudgetData.frequency}
              onChange={e => setEditBudgetData({ ...editBudgetData, frequency: e.target.value })}
              className="w-full px-3 py-2 border rounded"
              required
            >
              <option value="">Select Frequency</option>
              {FREQUENCY_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-lg font-medium text-gray-700 mb-1">Planned Cost</label>
            <input
              type="number"
              value={editBudgetData.plannedCost}
              onChange={e => setEditBudgetData({ ...editBudgetData, plannedCost: e.target.value })}
              className="w-full px-3 py-2 border rounded"
              min="0"
              step="0.01"
              required
            />
          </div>
          <div>
            <label className="block text-lg font-medium text-gray-700 mb-1">Actual Cost</label>
            <input
              type="number"
              value={editBudgetData.actualCost}
              onChange={e => setEditBudgetData({ ...editBudgetData, actualCost: e.target.value })}
              className="w-full px-3 py-2 border rounded"
              min="0"
              step="0.01"
            />
          </div>
          <div>
            <label className="block text-lg font-medium text-gray-700 mb-1">Account</label>
            <select
              value={editBudgetData.paymentAccountId}
              onChange={e => setEditBudgetData({ ...editBudgetData, paymentAccountId: e.target.value })}
              className="w-full px-3 py-2 border rounded"
              required
            >
              <option value="">Select Account</option>
              {accounts
                .map(acc => (
                  <option key={acc._id} value={acc._id}>{acc.name}</option>
                ))}
            </select>
          </div>
          <div>
            <label className="inline-flex items-center mt-2">
              <input
                type="checkbox"
                checked={editBudgetData.tags === "Subscription" || (Array.isArray(editBudgetData.tags) && editBudgetData.tags.includes("Subscription"))}
                onChange={e =>
                  setEditBudgetData({
                    ...editBudgetData,
                    tags: e.target.checked ? "Subscription" : ""
                  })
                }
                className="form-checkbox h-5 w-5 text-indigo-600"
              />
              <span className="ml-2 text-gray-700">Tag as Subscription</span>
            </label>
          </div>
        </div>
      </EditModal>

      <EditModal
        show={showAddForm}
        title="Add Item"
        onCancel={() => setShowAddForm(false)}
        onSubmit={handleAddBudget}
        loading={isAdding}
        submitLabel="Create"
      >
        <div className="space-y-4 max-w-md w-full max-h-[80vh] overflow-y-auto p-2 sm:p-0">
          {budgetError && (
            <div className="text-yellow-600 text-sm font-semibold mb-2">{budgetError}</div>
          )}
          <div>
            <label className="block text-lg font-medium text-gray-700 mb-1">Item</label>
            <input
              type="text"
              value={addBudgetData.itemName}
              onChange={e => setAddBudgetData({ ...addBudgetData, itemName: e.target.value })}
              className="w-full px-3 py-2 border rounded"
              required
            />
          </div>
          <div>
            <label className="block text-lg font-medium text-gray-700 mb-1">Category</label>
            <div className="relative">
              <input
                type="text"
                value={addBudgetData.category}
                onChange={e => handleCategoryChange(e.target.value)}
                className="w-full px-3 py-2 border rounded"
                required
              />
              {showCategorySuggestions && addBudgetData.category && (
                <div className="absolute z-10 bg-white border rounded shadow-md mt-1 max-h-60 overflow-auto w-full">
                  {categorySuggestions.length > 0 ? (
                    categorySuggestions.map((cat, idx) => (
                      <div
                        key={idx}
                        className="px-4 py-2 text-sm cursor-pointer hover:bg-gray-100"
                        onClick={() => selectCategory(cat)}
                      >
                        {cat}
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-2 text-sm text-gray-500">No suggestions found</div>
                  )}
                </div>
              )}
            </div>
          </div>
          <div>
            <label className="block text-lg font-medium text-gray-700 mb-1">Frequency</label>
            <select
              value={addBudgetData.frequency}
              onChange={e => setAddBudgetData({ ...addBudgetData, frequency: e.target.value })}
              className="w-full px-3 py-2 border rounded"
              required
            >
              <option value="">Select Frequency</option>
              {FREQUENCY_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-lg font-medium text-gray-700 mb-1">Planned Cost</label>
            <input
              type="number"
              value={addBudgetData.plannedCost}
              onChange={e => setAddBudgetData({ ...addBudgetData, plannedCost: e.target.value })}
              className="w-full px-3 py-2 border rounded"
              min="0"
              step="0.01"
              required
            />
          </div>
          <div>
            <label className="block text-lg font-medium text-gray-700 mb-1">Actual Cost</label>
            <input
              type="number"
              value={addBudgetData.actualCost}
              onChange={e => setAddBudgetData({ ...addBudgetData, actualCost: e.target.value })}
              className="w-full px-3 py-2 border rounded"
              min="0"
              step="0.01"
            />
          </div>
          <div>
            <label className="block text-lg font-medium text-gray-700 mb-1">Account</label>
            <select
              value={addBudgetData.paymentAccountId}
              onChange={e => setAddBudgetData({ ...addBudgetData, paymentAccountId: e.target.value })}
              className="w-full px-3 py-2 border rounded"
              required
            >
              <option value="">Select Account</option>
              {accounts
                .map(acc => (
                  <option key={acc._id} value={acc._id}>{acc.name}</option>
                ))}
            </select>
          </div>
          <div>
            <label className="inline-flex items-center mt-2">
              <input
                type="checkbox"
                checked={addBudgetData.tags === "Subscription" || (Array.isArray(addBudgetData.tags) && addBudgetData.tags.includes("Subscription"))}
                onChange={e =>
                  setAddBudgetData({
                    ...addBudgetData,
                    tags: e.target.checked ? "Subscription" : ""
                  })
                }
                className="form-checkbox h-5 w-5 text-indigo-600"
              />
              <span className="ml-2 text-gray-700">Tag as Subscription</span>
            </label>
          </div>
        </div>
      </EditModal>

      <InstructionsModal
        show={showInstructions}
        onClose={() => setShowInstructions(false)}
        type="budget"
        onDownloadTemplate={handleDownloadTemplate}
      />
    </div>
  );
};

export default BudgetContent;